import dayjs from 'dayjs'
import _ from 'lodash';
import reactStringReplace from 'react-string-replace';
import pupa from 'pupa'
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import JsFileDownloader from 'js-file-downloader';
import { PhoneNumberUtil } from 'google-libphonenumber';
import millify from 'millify';

import i18n from 'config/i18n';
import { validateUrl } from 'services/common/commonApi';
import { constants } from './constants';
import { StatusClassItem } from 'models/commonModel';

dayjs.extend(utc);
dayjs.extend(timezone);

const phoneUtil = PhoneNumberUtil.getInstance();

export function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text)
}

export const getFormattedDate = (dateString: string, format?: string) => {
    const date = new Date(dateString);
    const dateFormat = dayjs(date).format(format);
    return dateFormat;
}

export const applyTemplateNameRules = (name: string) => {
    if (name?.includes(' ')) {
        name = name?.replaceAll(' ', '_')
    }
    else {
        name = name?.replace(constants.petterns.invalidTemplateName, '')
    }
    return name.toLocaleLowerCase();
}

export const replaceTextWithNode = (text: string, node: React.ReactNode | React.ReactNode[]) => {
    return reactStringReplace(text, /\{\{(\d+)\}\}/g, (match, i) => {
        if (Array.isArray(node)) {
            return node[+match];
        }
        return node;
    });
}

export const stringReplace = (text: string, replaceWith: string[]) => {
    return pupa(text, replaceWith);
}

export const getBase64 = (file: File): Promise<string> => {
    var reader: any = new FileReader();
    reader.readAsDataURL(file);
    return new Promise((resolve, reject) => {
        reader.onload = () => {
            const baseURL = reader.result;
            resolve(removeBase64HTMLFormatting(baseURL));
        };
    });
}

export const getFileExt = (file: File) => {
    return file.name.split('.').pop() ?? '';
}

export const removeBase64HTMLFormatting = (base64String: string) => {
    let subStrings = base64String?.split(',') ?? []
    if (subStrings.length > 1)
        return subStrings[1]
    return base64String
}

export const isDateExpired = (expirationDate: string) => {
    const currentDate = new Date();
    const expirationDateTime = new Date(expirationDate);

    // Compare the current date with the expiration date
    return currentDate > expirationDateTime;
};

export const isNumber = (value: number | string) => {
    return !Number.isNaN(Number(value));
}

export const downloadFile = async (fileUrl: string, fileName?: string) => {
    // Trigger file download
    return new JsFileDownloader({ url: fileUrl, filename: fileName })
};

export const getFileExtension = (fileName: string) => {
    return fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
};

/**
 * Gets a phone number value after trimming country code from start, and leading zero
 * @param phoneNumber 
 * @param countryCode 
 * @returns 
 */
export const getPhoneNumberTrimmed = (phoneNumber: string, countryCode: string) => {
    let phone = phoneNumber.replace(/^0+/, '');
    let code = phone.substring(0, countryCode?.length);
    if (code === countryCode) {
        phone = phone.replace(countryCode, '');
    }
    return phone;
};

// // Function to sort the data by the specified column and order
export const getSortedData = (data: any[], columnName: string, order: 'asc' | 'desc') => {
    // Sorting function based on column type
    const sortingFunc = (item: any) => {
        const value = item[columnName];

        if (typeof value === 'string') {
            return value.toLocaleLowerCase();
        } else if (typeof value === 'number') {
            return value;
        } else {
            // Handle other data types if needed
            return value;
        }
    };

    // Sort the data based on the specified column and order
    const sortedData = _.orderBy(data, [sortingFunc], [order]);

    // Return the sorted data
    return sortedData;
};

export const getUtcDate = (utcDate: string, format?: string) => {
    return dayjs.utc(utcDate).format(format);
}

export const getLocalDate = (utcDate: string, format?: string) => {
    return dayjs.utc(utcDate).local().format(format);
}

const formatNumberWithPlus = (number: string) => {
    // Add a '+' at the start if it's not present
    if (!number.startsWith('+')) {
        return `+${number}`;
    }
    return number;
};

export const isValidNumber = (number: string, countryCode?: string) => {
    try {
        const parsedNumber = phoneUtil.parse(formatNumberWithPlus(number), countryCode);
        return phoneUtil.isValidNumber(parsedNumber);
    } catch (error) {
        return false;
    }
};

export const removeAllWhitespace = (str: string): string => {
    return str.replace(/\s/g, '');
};

const arabicUnits: Record<string, string> = {
    K: "ألف", // Thousand
    M: "مليون", // Million
    B: "مليار", // Billion
    T: "تريليون", // Trillion
};

const formatNumberInArabic = (num: number, precision: number = 1) => {
    const formatted = millify(num, { precision, locales: 'en-US' });
    return formatted.replace(/K|M|B|T/g, (match) => arabicUnits[match] || match);
};

export const formatLargeNumber = (number: number, precision: number = 1, lang: string = 'en'): string => {
    if (lang === 'ar')
        return formatNumberInArabic(number, precision)
    return millify(number, { precision })
}

export const urlExists = async (url: string, validTypes: string[]) => {
    if (!url) return false;
    try {
        return validateUrl(url, validTypes);
    } catch (error) {
        return false; // Return false for invalid or unreachable URLs
    }
}

export const translateHelper = (key: string) => {
    return i18n.t(key?.toLowerCase())
}

// Parses a string value to boolean
export const parseBoolean = (value: string) => value?.toString().toLocaleLowerCase() === "true";

export const getStatusClass = (
  status: string | number,
  statusClasses: StatusClassItem[],
  options?: {
    defaultClass?: string;
    extraClass?: string;
  }
) => {
  const matchedItem = statusClasses.find(
    (item) => item.status === status
  );

  return `${
    matchedItem?.className || options?.defaultClass || ''
  } ${options?.extraClass || ''}`.trim();
};